<script setup>
// Tabulação obrigatória (R1.23) no encerramento e na transferência (T-97), ajustada no T-99.
//
// Aparece SEMPRE, não só quando falta classificação. Quando a IA já classificou, os valores
// vêm marcados e o agente confirma ou corrige; quando não, ele escolhe. A diferença entre os
// dois casos aparece no texto, porque confirmar e classificar são pedidos diferentes e o
// agente precisa saber em qual está.
//
// A confirmação é o ponto: sem ela, o relatório da operação herda o palpite da IA sem ninguém
// ter olhado. E cada correção alimenta o aprendizado contínuo (R2.8).
//
// T-112 (21/08): entra a CAUSA RAIZ, o terceiro eixo da planilha do clube. Categoria e tipo
// dizem o assunto e a natureza do contato; a causa raiz diz POR QUE ele existiu, que é o que
// a operação usa para agir na origem.
//
// A ORDEM é CATEGORIA, CAUSA RAIZ, TIPO DE ATENDIMENTO (decisão do Douglas em 21/08, depois de
// ver as duas alternativas na tela). É um funil: a categoria RESTRINGE a lista de causas, de
// 161 para no máximo 32 (Ingressos), e quase sempre menos. Como a IA pré-classifica a categoria
// na triagem, o agente costuma abrir o modal com a lista já curta, sem ter feito nada.
//
// O tipo fecha porque, na planilha do clube, ele é consequência do cenário (tipo_inicial e
// tipo_escalonado da linha), não o que restringe a escolha.
//
// Custo conhecido da escolha: se a IA errar a categoria, a causa certa não aparece na lista e o
// agente precisa corrigir a categoria primeiro. Não trava nada (ela está logo acima e é
// editável, e trocá-la refiltra na hora), mas é um passo a mais nesse caso.
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useFandataTabulacao } from 'dashboard/composables/useFandataTabulacao';
import TaxonomiaAPI from 'dashboard/api/fandata/taxonomia';

const emit = defineEmits(['submit']);

const { t } = useI18n();
const {
  opcoes,
  garantirOpcoes,
  categoriaAtual,
  tipoAtual,
  causaAtual,
  causasDe,
} = useFandataTabulacao();
const currentChat = useMapGetter('getSelectedChat');

const dialogRef = ref(null);
const categoria = ref('');
const tipo = ref('');
const causa = ref('');
// Causa raiz provável, vinda do ranqueamento da própria planilha (mesma fonte do modal de
// pendência). Serve de atalho: o agente confirma em vez de procurar.
const sugestaoCausa = ref('');
// O motivo muda a frase de abertura: fechar um ticket e passar para outra pessoa são
// situações diferentes, e a razão de pedir a tabulação também.
const motivo = ref('encerrar');

// As causas da categoria escolhida, que é o que o seletor oferece.
const causasDisponiveis = computed(() => causasDe(categoria.value));
// Sem taxonomia importada, ou categoria sem causa na planilha, o campo não aparece e o
// encerramento segue: travar o fechamento por um campo que não existe seria defeito, não regra.
const pedeCausa = computed(() => causasDisponiveis.value.length > 0);
const pronto = computed(
  () =>
    Boolean(categoria.value && tipo.value) &&
    (!pedeCausa.value || Boolean(causa.value))
);
// Veio classificado pela IA? Muda o texto de "classifique" para "confirme".
const preClassificado = ref(false);

const open = async ({ motivo: novoMotivo = 'encerrar' } = {}) => {
  motivo.value = novoMotivo;
  await garantirOpcoes();
  // Reaproveita o que já existir: se a IA acertou a categoria e só faltou o tipo, o agente
  // confirma um campo em vez de dois.
  categoria.value = categoriaAtual.value || '';
  tipo.value = tipoAtual.value || '';
  causa.value = causaAtual.value || '';
  preClassificado.value = Boolean(categoria.value && tipo.value);
  dialogRef.value?.open();
  await nextTick();
  carregarSugestaoCausa();
};

// Silencioso de propósito: sem sugestão, o agente escolhe na lista. É atalho, não requisito.
const carregarSugestaoCausa = async () => {
  sugestaoCausa.value = '';
  if (!currentChat.value?.id) return;
  try {
    const { data } = await TaxonomiaAPI.sugestao(currentChat.value.id);
    sugestaoCausa.value =
      (data.sugestoes || [])
        .map(s => s.causa_raiz)
        .find(c => causasDisponiveis.value.includes(c)) || '';
    if (!causa.value) causa.value = sugestaoCausa.value;
  } catch (e) {
    sugestaoCausa.value = '';
  }
};

// Trocar a categoria invalida a causa escolhida sob a anterior. Deixar a antiga selecionada
// gravaria uma combinação que não existe na planilha.
watch(categoria, () => {
  if (causa.value && !causasDisponiveis.value.includes(causa.value)) {
    causa.value = '';
  }
  if (!causa.value && causasDisponiveis.value.includes(sugestaoCausa.value)) {
    causa.value = sugestaoCausa.value;
  }
});

const confirmar = () => {
  if (!pronto.value) return;
  emit('submit', {
    categoria: categoria.value,
    tipo: tipo.value,
    causaRaiz: causa.value || null,
  });
  dialogRef.value?.close();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="preClassificado
      ? t('CONVERSATION.TABULACAO.TITULO_CONFIRMAR')
      : t('CONVERSATION.TABULACAO.TITULO')"
    :confirm-button-label="t('CONVERSATION.TABULACAO.CONFIRMAR')"
    :disable-confirm-button="!pronto"
    @confirm="confirmar"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{
          {
            transferir: t('CONVERSATION.TABULACAO.MOTIVO_TRANSFERIR'),
            pendencia: t('CONVERSATION.TABULACAO.MOTIVO_PENDENCIA'),
          }[motivo] || t('CONVERSATION.TABULACAO.MOTIVO_ENCERRAR')
        }}
      </p>

      <!-- Dizer que veio da IA importa: o agente confirma com outro olhar quando sabe que
           aquilo é sugestão, e não algo que ele próprio escolheu antes. -->
      <p
        v-if="preClassificado"
        class="px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11"
      >
        {{ t('CONVERSATION.TABULACAO.PRE_CLASSIFICADO') }}
      </p>

      <div class="flex flex-col gap-1.5">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.TABULACAO.CATEGORIA') }}
        </span>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="c in opcoes.categorias"
            :key="c"
            type="button"
            class="px-2 py-1 text-xs font-medium capitalize border rounded-md transition-colors"
            :class="
              categoria === c
                ? 'bg-n-brand text-white border-n-brand'
                : 'bg-n-alpha-1 text-n-slate-11 border-n-weak hover:bg-n-alpha-2'
            "
            @click="categoria = c"
          >
            {{ c }}
          </button>
        </div>
      </div>

      <div v-if="pedeCausa" class="flex flex-col gap-1.5">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.TABULACAO.CAUSA_RAIZ') }}
        </span>
        <!-- Seletor com a seta explícita: sem ela o campo parece caixa de texto, e o agente
             não descobre que ali existe uma lista. -->
        <div class="relative">
          <select
            v-model="causa"
            class="w-full py-2 pl-2.5 pr-9 text-xs rounded-md border appearance-none bg-none cursor-pointer border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
          >
            <option value="">
              {{ t('CONVERSATION.TABULACAO.CAUSA_PLACEHOLDER') }}
            </option>
            <option v-for="c in causasDisponiveis" :key="c" :value="c">
              {{ c }}
            </option>
          </select>
          <div
            class="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none"
          >
            <span class="size-4 i-lucide-chevron-down text-n-slate-11" />
          </div>
        </div>
        <!-- Dizer que veio da planilha, pelo mesmo motivo do aviso da IA acima: o agente
             confere com outro olhar o que não foi ele que escolheu. -->
        <span
          v-if="sugestaoCausa && causa === sugestaoCausa"
          class="text-xs text-n-slate-11"
        >
          {{ t('CONVERSATION.TABULACAO.CAUSA_SUGERIDA') }}
        </span>
      </div>

      <div class="flex flex-col gap-1.5">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.TABULACAO.TIPO') }}
        </span>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="tp in opcoes.tipos"
            :key="tp"
            type="button"
            class="px-2 py-1 text-xs font-medium capitalize border rounded-md transition-colors"
            :class="
              tipo === tp
                ? 'bg-n-brand text-white border-n-brand'
                : 'bg-n-alpha-1 text-n-slate-11 border-n-weak hover:bg-n-alpha-2'
            "
            @click="tipo = tp"
          >
            {{ tp }}
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>
